import React, { useState, useEffect } from "react";
import {
  Search,
  Receipt,
  CheckCircle,
  XCircle,
  RefreshCw,
  Package,
  User,
  Calendar,
  DollarSign,
  ShoppingBag,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import * as ACTION from "../../services/actions";

const OrderSearchPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const dispatch = useDispatch();
  const receiptState = useSelector((state) => state?.allReceiptState);

  useEffect(() => {
    if (receiptState?.loading === false) {
      setLoading(false);
      if (receiptState?.error) {
        setError(receiptState.error);
        setSearchResult(null);
      } else if (receiptState?.data) {
        setSearchResult(receiptState);
        setError("");
      } else {
        setError("Order not found");
        setSearchResult(null);
      }
    }
  }, [receiptState]);

  console.log("receiptState.data", receiptState);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setError("Please enter an Order ID");
      return;
    }

    setLoading(true);
    setError("");
    setSearchResult(null);

    try {
      dispatch({
        type: `${ACTION.RECEIPT}_GET`,
        payload: {
          orderId: searchQuery.trim(),
        },
      });
    } catch (err) {
      console.error("Search error:", err);
      setError("Failed to search order");
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "delivered":
        return "bg-green-50 text-green-700 border border-green-200";
      case "processing":
        return "bg-orange-50 text-orange-700 border border-orange-200";
      case "cancelled":
        return "bg-red-50 text-red-700 border border-red-200";
      default:
        return "bg-gray-50 text-gray-700 border border-gray-200";
    }
  };

  const getPaymentStatusColor = (status) => {
    return status === "SUCCESS"
      ? "bg-green-50 text-green-700 border border-green-200"
      : "bg-red-50 text-red-700 border border-red-200";
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-blue-600 text-white shadow-lg">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <h1 className="text-2xl font-medium flex items-center gap-3">
            <Search className="h-7 w-7" />
            Order Search System
          </h1>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Search Card */}
        <div className="bg-white rounded-lg shadow-md mb-6 overflow-hidden">
          <div className="p-6">
            <h2 className="text-xl font-medium text-gray-800 mb-4 flex items-center gap-2">
              <Search className="h-5 w-5 text-blue-600" />
              Search Order
            </h2>

            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder=" "
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  className="w-full px-3 pt-5 pb-2 text-gray-900 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent peer"
                  id="orderid"
                />
                <label
                  htmlFor="orderid"
                  className="absolute left-3 top-3 text-gray-500 text-sm transition-all duration-200 peer-placeholder-shown:top-4 peer-placeholder-shown:text-base peer-focus:top-1 peer-focus:text-sm peer-focus:text-blue-600"
                >
                  Order ID *
                </label>
              </div>

              <button
                onClick={handleSearch}
                disabled={loading}
                className="px-8 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2 min-w-[160px] shadow-md hover:shadow-lg"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span className="font-medium">SEARCHING</span>
                  </>
                ) : (
                  <>
                    <Search className="h-5 w-5" />
                    <span className="font-medium">SEARCH</span>
                  </>
                )}
              </button>
            </div>

            <p className="text-sm text-gray-600 mt-3">
              Try: ORD001, ORD002, or ORD003 for demo
            </p>
          </div>
        </div>

        {/* Error Snackbar */}
        {error && (
          <div className="bg-red-600 text-white rounded-md p-4 mb-6 shadow-lg flex items-center gap-3">
            <XCircle className="h-5 w-5" />
            <span className="font-medium">{error}</span>
          </div>
        )}

        {/* Results */}
        {searchResult && (
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="bg-blue-50 px-6 py-4 border-b border-blue-100">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h3 className="text-xl font-medium text-gray-800 flex items-center gap-2">
                  <Package className="h-6 w-6 text-blue-600" />
                  Order Details
                </h3>
                <div
                  className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(
                    searchResult.status
                  )}`}
                >
                  <div className="w-2 h-2 rounded-full bg-current mr-2"></div>
                  {searchResult.status?.toUpperCase() || "UNKNOWN"}
                </div>
              </div>
            </div>

            <div className="p-6">
              {/* Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* Order ID */}
                <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-500">
                  <div className="flex items-center gap-3 mb-2">
                    <ShoppingBag className="h-5 w-5 text-blue-600" />
                    <span className="text-sm font-medium text-gray-600 uppercase tracking-wide">
                      Order ID
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {searchResult.orderId}
                  </p>
                </div>

                {/* Customer */}
                <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-green-500">
                  <div className="flex items-center gap-3 mb-2">
                    <User className="h-5 w-5 text-green-600" />
                    <span className="text-sm font-medium text-gray-600 uppercase tracking-wide">
                      Customer
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {searchResult.customerName || "N/A"}
                  </p>
                </div>

                {/* Date */}
                <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-purple-500">
                  <div className="flex items-center gap-3 mb-2">
                    <Calendar className="h-5 w-5 text-purple-600" />
                    <span className="text-sm font-medium text-gray-600 uppercase tracking-wide">
                      Order Date
                    </span>
                  </div>
                  <p className="text-xl font-semibold text-gray-900">
                    {searchResult.date
                      ? new Date(searchResult.date).toLocaleDateString(
                          "en-US",
                          {
                            weekday: "long",
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          }
                        )
                      : "N/A"}
                  </p>
                </div>

                {/* Amount */}
                <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-orange-500">
                  <div className="flex items-center gap-3 mb-2">
                    <DollarSign className="h-5 w-5 text-orange-600" />
                    <span className="text-sm font-medium text-gray-600 uppercase tracking-wide">
                      Total Amount
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-green-600">
                    ₹
                    {searchResult.amount
                      ? searchResult.amount.toFixed(2)
                      : "0.00"}
                  </p>
                </div>
              </div>

              {/* Payment Status */}
              <div className="mb-6">
                <h4 className="text-lg font-medium text-gray-800 mb-3">
                  Payment Information
                </h4>
                <div
                  className={`inline-flex items-center px-4 py-3 rounded-lg text-sm font-medium ${getPaymentStatusColor(
                    searchResult.paymentStatus
                  )} shadow-sm`}
                >
                  {searchResult.paymentStatus === "SUCCESS" ? (
                    <CheckCircle className="h-5 w-5 mr-3" />
                  ) : (
                    <XCircle className="h-5 w-5 mr-3" />
                  )}
                  <span className="font-semibold">
                    Payment {searchResult.paymentStatus || "UNKNOWN"}
                  </span>
                </div>
              </div>

              {/* Receipt */}
              <div className="mb-6">
                <h4 className="text-lg font-medium text-gray-800 mb-3">
                  <Receipt className="h-5 w-5 inline mr-2" />
                  Receipt Information
                </h4>
                {searchResult.receiptNumber ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-sm text-green-700 font-medium">
                      Receipt Generated
                    </p>
                    <p className="text-lg font-mono font-bold text-green-800">
                      {searchResult.receiptNumber}
                    </p>
                  </div>
                ) : (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <p className="text-sm text-orange-700 font-medium">
                      No Receipt Available
                    </p>
                    <p className="text-sm text-orange-600">
                      Receipt number not generated yet
                    </p>
                  </div>
                )}
              </div>

              {/* Items */}
              {searchResult.items && searchResult.items.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-lg font-medium text-gray-800 mb-3">
                    Order Items
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {searchResult.items.map((item, index) => (
                      <div
                        key={index}
                        className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-3"
                      >
                        <div className="bg-blue-100 p-2 rounded-full">
                          <Package className="h-4 w-4 text-blue-600" />
                        </div>
                        <span className="font-medium text-blue-800">
                          {item}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Repost Button */}
              {searchResult.paymentStatus === "SUCCESS" &&
                !searchResult.receiptNumber && (
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                      <h5 className="font-medium text-blue-800 mb-1">
                        Action Required
                      </h5>
                      <p className="text-sm text-blue-700">
                        Payment was successful but receipt was not generated.
                        Click below to request a repost of the receipt.
                      </p>
                    </div>
                    <button
                      disabled
                      className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-lg font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-300 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50"
                    >
                      <RefreshCw className="h-5 w-5 mr-3" />
                      REQUEST REPOST
                    </button>
                  </div>
                )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="text-sm text-gray-600 flex items-center justify-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span>
                PWA Ready - Works offline for enhanced user experience
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSearchPage;
